import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;
const Container = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const CarouselImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
`;
const Controls = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  align-items: center;
`;
const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;
const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 12px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const PrevButton = styled(NavigationButton)`
  left: 20px;
`;
const NextButton = styled(NavigationButton)`
  right: 20px;
`;
const ImageCounter = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
`;
const Title = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  max-width: 400px;
`;

const VehicleImagesCarousel = ({ images = [], isOpen, onClose, title, initialIndex = 0 }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(initialIndex);

  useEffect(() => {
    if (isOpen) setCurrentImageIndex(initialIndex);
  }, [isOpen, initialIndex]);

  // Preload next and previous images
  useEffect(() => {
    if (!images || images.length === 0) {
      return;
    }
    const nextIdx = (currentImageIndex + 1) % images.length;
    const prevIdx = (currentImageIndex - 1 + images.length) % images.length;
    [nextIdx, prevIdx].forEach(idx => {
      if (images[idx] && images[idx].url) {
        const img = new window.Image();
        img.src = images[idx].url;
      }
    });
  }, [currentImageIndex, images]);

  const handlePrevious = () => {
    setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !images || images.length === 0) {
    return null;
  }

  return (
    <Modal onClick={onClose}>
      <Container onClick={e => e.stopPropagation()}>
        {title && <Title>{title}</Title>}
        <Controls>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Controls>
        <CarouselImage
          src={images[currentImageIndex].url}
          alt={`Vehicle Image ${currentImageIndex + 1}`}
          onError={e => {
            e.target.src = '/car-placeholder.png';
          }}
        />
        <PrevButton onClick={handlePrevious} disabled={images.length <= 1}>
          <ChevronLeft size={24} />
        </PrevButton>
        <NextButton onClick={handleNext} disabled={images.length <= 1}>
          <ChevronRight size={24} />
        </NextButton>
        <ImageCounter>
          {currentImageIndex + 1} of {images.length}
        </ImageCounter>
      </Container>
    </Modal>
  );
};

export default VehicleImagesCarousel;
